import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Avatar,
  Alert, Snackbar, CircularProgress, IconButton, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';

export default function ProfilePage() {
  const { isReadOnly } = useReadOnly();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  // Profile fields
  const [profile, setProfile] = useState({
    email: '',
    givenName: '',
    familyName: '',
    phoneNumber: '',
    picture: '',
    role: '',
  });

  // Password fields
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Uploading avatar
  const [uploading, setUploading] = useState(false);

  // Notification preference
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    tenantApi.getMyProfile()
      .then((data) => {
        const u = data.user || {};
        setProfile({
          email: u.email || '',
          givenName: u.givenName || '',
          familyName: u.familyName || '',
          phoneNumber: u.phoneNumber || '',
          picture: u.picture || '',
          role: u.role || '',
        });
        setNotificationsEnabled(u.notificationsEnabled !== false);
      })
      .catch((e) => setToast({ open: true, message: e.message, severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await tenantApi.updateMyProfile({
        givenName: profile.givenName,
        familyName: profile.familyName,
        phoneNumber: profile.phoneNumber || undefined,
      });
      setToast({ open: true, message: 'Profile updated.', severity: 'success' });
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleNotifications = async (next) => {
    setNotificationsEnabled(next); // optimistic
    setSavingNotifications(true);
    try {
      await tenantApi.updateMyProfile({ notificationsEnabled: next });
      setToast({ open: true, message: `Notifications ${next ? 'enabled' : 'disabled'}.`, severity: 'success' });
    } catch (e) {
      setNotificationsEnabled(!next); // revert
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({ open: true, message: 'New passwords do not match.', severity: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setToast({ open: true, message: 'Password must be at least 8 characters.', severity: 'error' });
      return;
    }
    setChangingPassword(true);
    try {
      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({
        oldPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setToast({ open: true, message: 'Password changed successfully.', severity: 'success' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      const msg = e.name === 'NotAuthorizedException'
        ? 'Current password is incorrect.'
        : e.message;
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { return; }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setToast({ open: true, message: 'Image must be under 2MB.', severity: 'error' });
      return;
    }

    setUploading(true);
    try {
      // Get presigned upload URL
      const { uploadUrl, pictureUrl } = await tenantApi.getAvatarUploadUrl(file.type);

      // Upload directly to S3
      const resp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!resp.ok) { throw new Error('Upload failed.'); }

      // Update Cognito profile with the picture URL
      await tenantApi.updateMyProfile({ picture: pictureUrl });
      setProfile((p) => ({ ...p, picture: pictureUrl }));
      setToast({ open: true, message: 'Profile picture updated.', severity: 'success' });
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 700 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>Profile</Typography>

      {/* Avatar + Name Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center" mb={3}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={profile.picture}
              alt={profile.givenName || profile.email}
              sx={{ width: 80, height: 80, fontSize: 32 }}
            >
              {(profile.givenName || profile.email || '?')[0].toUpperCase()}
            </Avatar>
            <Tooltip title="Change photo">
              <IconButton
                size="small"
                sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                }}
                onClick={() => fileInputRef.current?.click()}
                disabled={isReadOnly || uploading}
              >
                {uploading ? <CircularProgress size={16} /> : <PhotoCameraIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={handleAvatarSelect}
            />
          </Box>
          <Box>
            <Typography variant="h6">{profile.givenName || profile.familyName ? `${profile.givenName} ${profile.familyName}`.trim() : profile.email}</Typography>
            <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{profile.role}</Typography>
          </Box>
        </Stack>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              size="small"
              fullWidth
              value={profile.givenName}
              onChange={(e) => setProfile((p) => ({ ...p, givenName: e.target.value }))}
              disabled={isReadOnly}
            />
            <TextField
              label="Last Name"
              size="small"
              fullWidth
              value={profile.familyName}
              onChange={(e) => setProfile((p) => ({ ...p, familyName: e.target.value }))}
              disabled={isReadOnly}
            />
          </Stack>
          <TextField
            label="Email"
            size="small"
            fullWidth
            value={profile.email}
            disabled
            helperText="Email cannot be changed."
          />
          <TextField
            label="Phone Number"
            size="small"
            fullWidth
            value={profile.phoneNumber}
            onChange={(e) => setProfile((p) => ({ ...p, phoneNumber: e.target.value }))}
            placeholder="+12025551234"
            helperText="E.164 format (e.g., +12025551234)"
            disabled={isReadOnly}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={handleSaveProfile} disabled={isReadOnly || saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Notifications Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>Notifications</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={notificationsEnabled}
              onChange={(e) => handleToggleNotifications(e.target.checked)}
              disabled={isReadOnly || savingNotifications}
            />
          }
          label="Receive system notifications (low stock, usage warnings, and more)"
        />
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          When off, you won&apos;t receive new notification deliveries. Past notifications stay visible on the Notifications page.
        </Typography>
      </Paper>

      {/* Change Password Section */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Change Password</Typography>
        <Stack spacing={2}>
          <TextField
            label="Current Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))}
            disabled={isReadOnly}
          />
          <TextField
            label="New Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
            disabled={isReadOnly}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
            error={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword}
            helperText={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword ? 'Passwords do not match' : ''}
            disabled={isReadOnly}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleChangePassword}
              disabled={isReadOnly || changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
